import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  Input,
  Option,
  Select,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowRight24Regular,
  Search24Regular,
  TicketDiagonal24Regular,
} from '@fluentui/react-icons'
import { api, AdminOverviewResponse, AdminTicket } from '../lib/api'
import { useConfig } from '../context/ConfigContext'
import { TICKET_STATUS_KEYS, ticketStatusColor, ticketStatusLabel } from '../lib/ticket-status'

const useStyles = makeStyles({
  root: {
    maxWidth: '1100px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    marginTop: '4px',
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  statCard: {
    padding: '14px',
  },
  statLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  statValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    marginTop: '6px',
  },
  controls: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 0.8fr 0.8fr',
    gap: '10px',
  },
  tickets: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  ticketCard: {
    padding: '14px',
    cursor: 'pointer',
  },
  ticketHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  ticketMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
  },
  ticketNumber: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  ticketSubject: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ticketMeta: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  noAccess: {
    padding: '24px',
    textAlign: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px',
  },
})

function formatDate(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })
}

export function AdminDashboard() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { config } = useConfig()

  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null)
  const [tickets, setTickets] = useState<AdminTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [search, setSearch] = useState('')

  const isClientAdmin = !!config?.access?.isClientAdmin

  const canLoad = useMemo(() => isClientAdmin, [isClientAdmin])

  useEffect(() => {
    if (!canLoad) {
      setLoading(false)
      return
    }

    const run = async () => {
      setLoading(true)
      try {
        const [ov, ticketsResult] = await Promise.all([
          api.getAdminOverview(),
          api.getAdminTickets({
            status: statusFilter === 'all' ? undefined : statusFilter,
            ticketType: typeFilter === 'all' ? undefined : typeFilter,
            search: search.trim() || undefined,
            limit: 50,
            offset: 0,
          }),
        ])
        setOverview(ov)
        setTickets(ticketsResult.tickets)
      } catch (err) {
        console.error('Failed to load admin dashboard:', err)
      } finally {
        setLoading(false)
      }
    }

    run()
  }, [canLoad, statusFilter, typeFilter, search])

  if (!isClientAdmin) {
    return (
      <Card className={styles.noAccess}>
        <h2 className={styles.title}>Admin Dashboard</h2>
        <p className={styles.subtitle}>You do not currently have admin access for this client.</p>
      </Card>
    )
  }

  if (loading && !overview) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading admin dashboard..." />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.titleRow}>
        <div>
          <h1 className={styles.title}>Admin Dashboard</h1>
          <div className={styles.subtitle}>
            {overview?.client?.name || 'Client'} ticket overview and progress tracking
          </div>
        </div>
      </div>

      <div className={styles.statGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statLabel}>Active Tickets</div>
          <div className={styles.statValue}>{overview?.summary.activeTickets ?? 0}</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statLabel}>Total Tickets</div>
          <div className={styles.statValue}>{overview?.summary.totalTickets ?? 0}</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statLabel}>{ticketStatusLabel('resolved', config?.statusLabels)}</div>
          <div className={styles.statValue}>{overview?.summary.resolvedTickets ?? 0}</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statLabel}>Avg First Response</div>
          <div className={styles.statValue}>
            {overview?.summary.avgFirstResponseMinutes != null
              ? `${Math.round(overview.summary.avgFirstResponseMinutes)}m`
              : 'N/A'}
          </div>
        </Card>
      </div>

      <div className={styles.controls}>
        <Input
          value={search}
          contentBefore={<Search24Regular />}
          placeholder="Search tickets by number, subject, or sender"
          onChange={(_, d) => setSearch(d.value)}
        />
        <Select value={typeFilter} onChange={(_, d) => setTypeFilter(d.value)}>
          <Option value="all">All Types</Option>
          <Option value="support">Support</Option>
          <Option value="it_request">IT Requests</Option>
        </Select>
        <Select value={statusFilter} onChange={(_, d) => setStatusFilter(d.value)}>
          <Option value="all">All Statuses</Option>
          {TICKET_STATUS_KEYS.map((key) => (
            <Option key={key} value={key}>{ticketStatusLabel(key, config?.statusLabels)}</Option>
          ))}
        </Select>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner label="Refreshing tickets..." />
        </div>
      ) : tickets.length === 0 ? (
        <Card className={styles.noAccess}>
          <TicketDiagonal24Regular style={{ fontSize: 32 }} />
          <div className={styles.subtitle}>No tickets found for the selected filters.</div>
        </Card>
      ) : (
        <div className={styles.tickets}>
          {tickets.map((ticket) => {
            const status = {
              label: ticket.statusLabel || ticketStatusLabel(ticket.status, config?.statusLabels),
              color: ticketStatusColor(ticket.status),
            }
            return (
              <Card
                key={ticket.id}
                className={styles.ticketCard}
                onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
              >
                <div className={styles.ticketHeader}>
                  <div className={styles.ticketMain}>
                    <span className={styles.ticketNumber}>{ticket.ticketNumber}</span>
                    <span className={styles.ticketSubject}>{ticket.subject}</span>
                  </div>
                  <Badge color={status.color} appearance="filled">
                    {status.label}
                  </Badge>
                </div>
                <div className={styles.ticketMeta}>
                  <span>{ticket.ticketType === 'it_request' ? 'IT Request' : 'Support'}</span>
                  <span>Priority: {ticket.priority}</span>
                  <span>Updated: {formatDate(ticket.updatedAt)}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                    Open
                    <ArrowRight24Regular />
                  </span>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button appearance="subtle" onClick={() => navigate('/tickets')}>
          Switch to My Tickets
        </Button>
      </div>
    </div>
  )
}
